import React, { useState } from "react";
import { MousePointerClick, Plus, Trash2 } from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../../../UI";
import { Field, IconButton, InfoTip, JsonEditor, KeyValueEditor, LinkButton, NumberInput, optionsTip, Section, SelectInput, Stat, StatusMessage, TextInput, useStatus, WithInfo } from "../common";
import { RELATION_LABELS } from "../constants";
import { GLOSSARY } from "../glossary";
import { setIn } from "../helpers";
import { autoMapSource, describeAutoMap, sameExtract } from "./autoMap";
import ResponseExplorer from "./ResponseExplorer";

const RELATION_TIP = optionsTip(GLOSSARY.structure.relation, GLOSSARY.relations, RELATION_LABELS);
const PREPARE_TIP = optionsTip(GLOSSARY.structure.prepare, GLOSSARY.structure.prepareOps);

// Clicked node → a selection path: indexes become [*], and a list gets [*].
const toSelectPath = (path, value) => {
    const generic = path.replace(/\[\d+\]/g, "[*]");
    return Array.isArray(value) ? `${generic}[*]` : generic;
};

// Makes a clicked path relative to the product selection: $.products[*].variants → @.variants[*]
const toRelativeSelect = (path, value, productSelect) => {
    const select = toSelectPath(path, value);
    const base = (productSelect || "").trim();
    if (base && select.startsWith(base) && select !== base) return `@${select.slice(base.length)}`;
    return select;
};

const PREPARE_EXTRA = {
    pivot: [["name", "Name key", "name"], ["value", "Value key", "value"], ["into", "Write to key (optional)", ""]],
    rename: [["from", "From", ""], ["to", "To", ""]],
};

const PICK_ACTIVE = "text-primary bg-blue-50 ring-1 ring-primary";

const StepsEditor = ({ steps = [], onChange, bindings, onPickFor, activeIndex }) => (
    <div className="flex flex-col gap-2">
        {steps.length > 0 && (
            <div className="grid grid-cols-[110px_1fr_120px_auto] gap-2 text-xs text-gray-500">
                <span><WithInfo tip={GLOSSARY.structure.from}>From</WithInfo></span>
                <span><WithInfo tip={GLOSSARY.structure.select}>Path</WithInfo></span>
                <span><WithInfo tip={GLOSSARY.structure.as}>Name (as)</WithInfo></span>
                <span className="w-7" />
            </div>
        )}
        {steps.map((step, index) => (
            <div key={index} className="grid grid-cols-[110px_1fr_120px_auto] gap-2 items-center">
                <SelectInput
                    value={step.from || ""}
                    placeholder="document"
                    options={bindings.filter((b) => b !== step.as).map((b) => ({ value: b, label: `from ${b}` }))}
                    onChange={(v) => onChange(steps.map((s, i) => (i === index ? { ...s, from: v || undefined } : s)))}
                />
                <div className="flex gap-1">
                    <TextInput monospace value={step.select} placeholder={step.from ? "@.variants[*]" : "$.products[*]"} onChange={(v) => onChange(steps.map((s, i) => (i === index ? { ...s, select: v } : s)))} />
                    <IconButton title="Pick from the response" className={activeIndex === index ? PICK_ACTIVE : ""} onClick={() => onPickFor(index)}>
                        <MousePointerClick size={16} />
                    </IconButton>
                </div>
                <TextInput value={step.as} placeholder="as" onChange={(v) => onChange(steps.map((s, i) => (i === index ? { ...s, as: v || undefined } : s)))} />
                <IconButton title="Remove level" disabled={steps.length === 1} onClick={() => onChange(steps.filter((_, i) => i !== index))}>
                    <Trash2 size={16} />
                </IconButton>
            </div>
        ))}
    </div>
);

// Step 2: how to find products and variants.
const StructureStep = ({ source, update, draft, meta, testResult, extractResult, extracting, runExtract, onNext }) => {
    const [pick, setPick] = useState(null);
    const [status, showStatus] = useStatus(12000);
    const [checkStatus, showCheckStatus] = useStatus(12000);
    const [pickStatus, showPickStatus] = useStatus(6000);
    const extract = source.extract || {};
    const relation = extract.variants?.relation?.type || (extract.products?.group_by ? "group" : "self");
    const detection = testResult?.detection;
    const productSteps = extract.products?.steps || [];
    const variantSteps = extract.variants?.steps || [];
    const productSelect = productSteps[productSteps.length - 1]?.select;
    const productBindings = productSteps.map((s, i) => s.as || (i === productSteps.length - 1 ? "product" : `_step${i}`));
    const variantBindings = variantSteps.map((s, i) => s.as || (i === variantSteps.length - 1 ? "variant" : `_step${i}`));

    const setExtract = (path, value) => update((s) => setIn(s, ["extract", ...path], value));

    const setRelation = (type) => {
        update((s) => {
            let next = setIn(s, ["extract", "variants", "relation"], { type });
            if (type === "group") {
                next = setIn(next, ["extract", "products"], { group_by: { path: "$variant.parentId" } });
                next = setIn(next, ["extract", "variants", "steps"], [{ select: productSelect || "$.items[*]", as: "variant" }]);
            } else if (relation === "group") {
                next = setIn(next, ["extract", "products"], { steps: [{ select: variantSteps[0]?.select || "$.products[*]", as: "product" }] });
            }
            if (type === "contained" && !next.extract.variants.steps?.length) {
                next = setIn(next, ["extract", "variants", "steps"], [{ from: "product", select: "@.variants[*]", as: "variant" }]);
            }
            if (type === "key" && !next.extract.variants.steps?.length) {
                next = setIn(next, ["extract", "variants", "steps"], [{ select: "$.variants[*]", as: "variant" }]);
            }
            if (["self", "zip", "cartesian"].includes(type)) next = setIn(next, ["extract", "variants", "steps"], undefined);
            return next;
        });
    };

    // Applies a suggestion, optionally auto-maps, then re-checks extraction so
    // the result is visible straight away.
    const applySuggestion = async (suggestion, autoMap) => {
        let next = { ...source, extract: JSON.parse(JSON.stringify(suggestion.extract)) };
        let summary = `Using "${suggestion.label}".`;
        if (autoMap) {
            const result = autoMapSource(next, draft.model, suggestion);
            next = result.source;
            summary += ` ${describeAutoMap(result.mapped)}`;
        }
        update(next);
        showStatus(`${summary} Checking extraction...`, "info");
        const { data, error } = await runExtract(next);
        if (error) showStatus(`${summary} The extraction check failed: ${error}`, "error");
        else showStatus(`${summary} Found ${data?.totals?.products ?? 0} products and ${data?.totals?.variants ?? 0} variants.`);
    };

    const startPick = (target) => {
        if (!testResult?.sample) {
            showPickStatus("Test the request first (step 1) to explore the response.", "error");
            return;
        }
        setPick(pick && pick.kind === target.kind && pick.index === target.index ? null : target);
    };

    const onPickNode = (path, value) => {
        if (!pick) return;
        let chosen;
        if (pick.kind === "product-step") {
            chosen = toSelectPath(path, value);
            setExtract(["products", "steps", pick.index, "select"], chosen);
        } else if (pick.kind === "variant-step") {
            const step = variantSteps[pick.index];
            chosen = step?.from || relation === "contained" ? toRelativeSelect(path, value, pick.index === 0 ? productSelect : variantSteps[pick.index - 1]?.select) : toSelectPath(path, value);
            setExtract(["variants", "steps", pick.index, "select"], chosen);
        } else if (pick.kind === "prepare") {
            chosen = path.replace(/\[\d+\]/g, "[*]");
            update((s) => setIn(s, ["prepare", pick.index, "at"], chosen));
        }
        showPickStatus(`Set the ${pick.label} to ${chosen}.`);
        setPick(null);
    };

    const checkExtraction = async () => {
        showCheckStatus("Running extraction...", "info");
        const { data, error } = await runExtract();
        if (error) return showCheckStatus(`Extraction failed: ${error}`, "error");
        const products = data?.totals?.products ?? 0;
        showCheckStatus(
            products ? `Found ${products} products and ${data?.totals?.variants ?? 0} variants.` : "No products found. Check the product path and relation.",
            products ? "success" : "error"
        );
    };

    const prepare = source.prepare || [];

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-4">
            <div className="flex flex-col gap-4 min-w-0">
                {pick && (
                    <div className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
                        <span>Click a node in the response explorer to set the {pick.label}.</span>
                        <LinkButton onClick={() => setPick(null)}>Cancel</LinkButton>
                    </div>
                )}
                <StatusMessage status={pickStatus} />
                {!detection && (
                    <p className="text-sm text-gray-500">Test the request in step 1 to get structure suggestions and the response explorer.</p>
                )}
                {detection && !detection.suggestions?.length && (
                    <p className="text-sm text-yellow-700">No product lists were detected in the test response. Set the paths below by hand.</p>
                )}
                {detection?.suggestions?.length > 0 && (
                    <Section title="Suggested structures" description="Found by scanning the whole test response." tip={optionsTip(GLOSSARY.structure.suggestions, { orphans: GLOSSARY.stats.orphans }, { orphans: "Orphans" })}>
                        <StatusMessage status={status} />
                        {detection.suggestions.map((s, i) => (
                            <div key={i} className={`flex flex-wrap items-center justify-between gap-2 text-sm border-b border-gray-100 pb-2 ${sameExtract(s.extract, source.extract) ? "bg-green-50 -mx-2 px-2 rounded" : ""}`}>
                                <div className="min-w-0">
                                    <p className="font-medium flex items-center gap-2">
                                        {s.label}
                                        {sameExtract(s.extract, source.extract) && <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">In use</span>}
                                    </p>
                                    <p className="font-mono text-xs text-gray-500 break-all">
                                        {s.products_path}{s.variants_path ? ` → ${s.variants_path}` : ""}{s.group_by ? ` (by ${s.group_by})` : ""}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {RELATION_LABELS[s.type]?.split(" (")[0] || s.type} · {s.counts.products} products · {s.counts.variants} variants · {s.counts.orphans} orphans
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <SecondaryButton size="xs" disable={extracting} handleClick={() => applySuggestion(s, false)}>Use</SecondaryButton>
                                    <PrimaryButton size="xs" disable={extracting} handleClick={() => applySuggestion(s, true)}>Use + auto-map</PrimaryButton>
                                </div>
                            </div>
                        ))}
                        {(detection.prepare_hints || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 text-xs">
                                <span className="text-gray-500 inline-flex items-center gap-1">
                                    Detected <InfoTip content={optionsTip(`${GLOSSARY.structure.prepare} Click one to add it.`, GLOSSARY.structure.prepareOps)} wide />:
                                </span>
                                {detection.prepare_hints.map((hint, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 hover:bg-blue-100 font-mono"
                                        onClick={() => {
                                            update((s) => ({ ...s, prepare: [...(s.prepare || []), { op: hint.op, at: hint.at, ...(hint.name ? { name: hint.name, value: hint.value } : {}) }] }));
                                            showStatus(`Added a ${hint.op} step for ${hint.at}. Check extraction to see its effect.`);
                                        }}
                                    >
                                        + {hint.op} {hint.at}
                                    </button>
                                ))}
                            </div>
                        )}
                        {(detection.pagination_hints || []).length > 0 && (
                            <p className="text-xs text-gray-500">
                                Pagination hints <InfoTip content={`${GLOSSARY.request.pagination} Set it up in step 1.`} />: {detection.pagination_hints.map((h) => `${h.type} (${Object.values(h).slice(1).join(", ")})`).join(" · ")}
                            </p>
                        )}
                    </Section>
                )}

                <Section title="Prepare" description="Optional steps run on each response before extraction." tip={PREPARE_TIP}>
                    {prepare.map((step, index) => (
                        <div key={index} className="flex flex-wrap items-center gap-2">
                            <SelectInput className="max-w-[190px]" value={step.op} options={meta?.prepare_ops || []} onChange={(v) => update((s) => setIn(s, ["prepare", index, "op"], v))} />
                            <div className="flex gap-1 flex-1 min-w-[200px]">
                                <TextInput monospace value={step.at} placeholder="$.products[*].specs" onChange={(v) => update((s) => setIn(s, ["prepare", index, "at"], v))} />
                                <IconButton
                                    title="Pick from the response"
                                    className={pick?.kind === "prepare" && pick.index === index ? PICK_ACTIVE : ""}
                                    onClick={() => startPick({ kind: "prepare", index, label: "prepare step path" })}
                                >
                                    <MousePointerClick size={16} />
                                </IconButton>
                            </div>
                            {(PREPARE_EXTRA[step.op] || []).map(([key, label, placeholder]) => (
                                <TextInput key={key} className="max-w-[140px]" value={step[key]} placeholder={placeholder || label} onChange={(v) => update((s) => setIn(s, ["prepare", index, key], v || undefined))} />
                            ))}
                            <IconButton title="Remove" onClick={() => update((s) => ({ ...s, prepare: prepare.filter((_, i) => i !== index) }))}>
                                <Trash2 size={16} />
                            </IconButton>
                        </div>
                    ))}
                    <LinkButton onClick={() => update((s) => ({ ...s, prepare: [...prepare, { op: "parse_embedded_json", at: "" }] }))}>
                        <Plus size={14} /> Add prepare step
                    </LinkButton>
                </Section>

                <Section title="Products and variants" tip={GLOSSARY.structure.steps}>
                    <Field label="Relation" tip={RELATION_TIP}>
                        <SelectInput value={relation} options={(meta?.relation_types || Object.keys(RELATION_LABELS)).map((r) => ({ value: r, label: RELATION_LABELS[r] || r }))} onChange={setRelation} />
                    </Field>

                    {relation === "group" ? (
                        <>
                            <Field label="Rows" help="The list of variant rows" tip={GLOSSARY.structure.rows}>
                                <StepsEditor
                                    steps={variantSteps}
                                    bindings={[]}
                                    activeIndex={pick?.kind === "variant-step" ? pick.index : undefined}
                                    onChange={(v) => setExtract(["variants", "steps"], v)}
                                    onPickFor={(index) => startPick({ kind: "variant-step", index, label: "row path" })}
                                />
                            </Field>
                            <Field label="Group rows by" help="Rows with the same value become one product, e.g. $variant.styleId" tip={GLOSSARY.structure.groupBy}>
                                <TextInput monospace value={extract.products?.group_by?.path ?? extract.products?.group_by} onChange={(v) => setExtract(["products", "group_by"], { path: v })} />
                            </Field>
                        </>
                    ) : (
                        <Field label="Products" help="Each level binds its nodes to a name (as) that field mappings can read, e.g. $product.brand" tip={GLOSSARY.structure.steps}>
                            <StepsEditor
                                steps={productSteps}
                                bindings={productBindings.slice(0, -1)}
                                activeIndex={pick?.kind === "product-step" ? pick.index : undefined}
                                onChange={(v) => setExtract(["products", "steps"], v)}
                                onPickFor={(index) => startPick({ kind: "product-step", index, label: "product path" })}
                            />
                            <LinkButton onClick={() => setExtract(["products", "steps"], [...productSteps, { from: productBindings[productBindings.length - 1], select: "@.items[*]", as: `level${productSteps.length + 1}` }])}>
                                <Plus size={14} /> Add level
                            </LinkButton>
                        </Field>
                    )}

                    {(relation === "contained" || relation === "key") && (
                        <Field
                            label="Variants"
                            help={relation === "contained" ? "Start from a product binding, e.g. from product → @.variants[*]. Add levels for colour → size structures." : "Variants are selected from the whole document."}
                            tip={GLOSSARY.relations[relation]}
                        >
                            <StepsEditor
                                steps={variantSteps}
                                bindings={relation === "contained" ? [...productBindings, ...variantBindings] : variantBindings}
                                activeIndex={pick?.kind === "variant-step" ? pick.index : undefined}
                                onChange={(v) => setExtract(["variants", "steps"], v)}
                                onPickFor={(index) => startPick({ kind: "variant-step", index, label: "variant path" })}
                            />
                            {relation === "contained" && (
                                <LinkButton onClick={() => setExtract(["variants", "steps"], [...variantSteps, { from: variantBindings[variantBindings.length - 1] || "product", select: "@.sizes[*]", as: `level${variantSteps.length + 1}` }])}>
                                    <Plus size={14} /> Add level
                                </LinkButton>
                            )}
                        </Field>
                    )}

                    {relation === "key" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Variant's product id" help="e.g. $variant.productId" tip={GLOSSARY.structure.variantProductId}>
                                <TextInput monospace value={extract.variants?.relation?.variant?.path ?? extract.variants?.relation?.variant} onChange={(v) => setExtract(["variants", "relation", "variant"], { path: v })} />
                            </Field>
                            <Field label="Product id (or leave empty and use the list below)" help="e.g. $product.id" tip={GLOSSARY.structure.productId}>
                                <TextInput monospace value={extract.variants?.relation?.product?.path ?? extract.variants?.relation?.product} onChange={(v) => setExtract(["variants", "relation", "product"], v ? { path: v } : undefined)} />
                            </Field>
                            <Field label="Product's list of variant ids (optional)" help="e.g. $product.variantIds[*]; then the variant field above is the variant's own id" tip={GLOSSARY.structure.productList}>
                                <TextInput monospace value={extract.variants?.relation?.product_list?.path ?? extract.variants?.relation?.product_list} onChange={(v) => setExtract(["variants", "relation", "product_list"], v ? { path: v } : undefined)} />
                            </Field>
                        </div>
                    )}

                    {relation === "zip" && (
                        <Field label="Parallel arrays" help="Name → path on the product, e.g. size → $product.sizes. Each position becomes one variant." tip={`${GLOSSARY.structure.zipArrays} ${GLOSSARY.structure.zipMismatch}`}>
                            <KeyValueEditor
                                items={Object.entries(extract.variants?.relation?.arrays || {}).map(([key, value]) => ({ key, value: value?.path ?? value }))}
                                keyPlaceholder="size"
                                valuePlaceholder="$product.sizes"
                                onChange={(items) => setExtract(["variants", "relation", "arrays"], Object.fromEntries(items.map((i) => [i.key, i.value])))}
                            />
                            <SelectInput
                                className="max-w-[260px]"
                                value={extract.variants?.relation?.on_length_mismatch || "shortest"}
                                options={[{ value: "shortest", label: "Different lengths: use the shortest" }, { value: "fail", label: "Different lengths: skip the product" }]}
                                onChange={(v) => setExtract(["variants", "relation", "on_length_mismatch"], v)}
                            />
                        </Field>
                    )}

                    {relation === "cartesian" && (
                        <Field label="Option lists" help="Name → path, e.g. colour → $product.options.colour[*]. Every combination becomes a variant." tip={GLOSSARY.structure.axes}>
                            <KeyValueEditor
                                items={Object.entries(extract.variants?.relation?.axes || {}).map(([key, value]) => ({ key, value: value?.path ?? value }))}
                                keyPlaceholder="colour"
                                valuePlaceholder="$product.options.colour[*]"
                                onChange={(items) => setExtract(["variants", "relation", "axes"], Object.fromEntries(items.map((i) => [i.key, i.value])))}
                            />
                            <Field label="Maximum combinations" tip={GLOSSARY.structure.maxCombinations}>
                                <NumberInput className="max-w-[160px]" value={extract.variants?.relation?.max} placeholder="500" onChange={(v) => setExtract(["variants", "relation", "max"], v)} />
                            </Field>
                        </Field>
                    )}

                    <Field label="Only keep products where (optional JSON condition)" help='e.g. {"path": "type", "neq": "service"}' tip={GLOSSARY.structure.where}>
                        <JsonEditor rows={3} value={extract.products?.where ?? null} onChange={(v) => setExtract(["products", "where"], v || undefined)} />
                    </Field>
                </Section>

                <div className="flex flex-wrap items-center gap-3">
                    <PrimaryButton size="sm" loading={extracting} handleClick={checkExtraction}>Check extraction</PrimaryButton>
                    <InfoTip content={GLOSSARY.structure.check} />
                    <SecondaryButton size="sm" handleClick={onNext}>Next: map fields</SecondaryButton>
                    <StatusMessage status={checkStatus} />
                </div>

                {extractResult && (
                    <Section title="Result">
                        {extractResult.error ? (
                            <p className="text-sm text-red-500">{extractResult.error}</p>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                <Stat label="Products" value={extractResult.totals?.products} />
                                <Stat label="Variants" value={extractResult.totals?.variants} />
                                <Stat label="Orphan variants" value={extractResult.stats?.orphans} tip={GLOSSARY.stats.orphans} />
                                <Stat label="Filtered out" value={extractResult.stats?.filtered} tip={GLOSSARY.stats.filtered} />
                                <Stat label="Duplicate products" value={extractResult.stats?.duplicate_products} tip={GLOSSARY.stats.duplicates} />
                                <Stat label="No product key" value={extractResult.stats?.missing_product_key} tip={GLOSSARY.stats.missingProductKey} />
                                <Stat label="No variant key" value={extractResult.stats?.missing_variant_key} tip={GLOSSARY.stats.missingVariantKey} />
                            </div>
                        )}
                    </Section>
                )}

                <details>
                    <summary className="text-sm font-medium text-primary cursor-pointer">Edit extraction as JSON</summary>
                    <JsonEditor rows={14} value={source.extract} onChange={(v) => update((s) => ({ ...s, extract: v }))} className="mt-2" />
                </details>
            </div>

            <div className="min-w-0">
                <Section title="Response explorer" description={pick ? `Click a node to set the ${pick.label}.` : "Use the pick buttons, then click a node."} tip={GLOSSARY.structure.explorer}>
                    <ResponseExplorer sample={testResult?.sample} detection={detection} onPick={onPickNode} />
                </Section>
            </div>
        </div>
    );
};

export default StructureStep;
